import Decimal from "decimal.js"
import PropTypes from "prop-types"
import React, { useContext, useEffect, useMemo, useRef, useState } from "react"
import { useForm } from "react-hook-form"
import { useMutation, useQuery } from "react-query"
import { useMarina } from "src/contexts/MarinaContext"
import MaxPaymentAmountDisplay from "src/main/Billing/MaxPaymentAmountDisplay"
import PaymentMethodDropdown from "src/main/Billing/PaymentMethodDropdown"
import RefundPaymentMethodDropdown, {
  formatPaymentMethods,
} from "src/main/Billing/RefundPaymentMethodDropdown"
import { SharedBillingContext } from "src/main/Billing/SharedBillingContext"
import { useGetRefundablePaymentMethods } from "src/main/Billing/hooks"

import AlertBanner from "src/components/AlertBanner"
import Button from "src/components/Button"
import Form from "src/components/Form"
import Modal from "src/components/Modal"
import ReloadableWidget from "src/components/ReloadableWidget"

import { createInvoiceTxn, getSurchargeCopy } from "src/api/Billing/Payments"

import { formatCurrencyFromMinorUnits } from "src/utils/UnitConversion"
import { capitalize } from "src/utils/string_helpers"

import {
  getRequestPaymentMethodId,
  paymentMethodIsCreditCard,
} from "../helpers"
import { OFFLINE_PAYMENT_METHODS, getTxnAttributes } from "./helpers"
import { useUpdateInvoice } from "./hooks"

const SURCHARGE_ELIGIBILITY_CHECK_ERROR =
  "We encountered an error determining if a surcharge would apply to this payment upon settle. Please contact Dockwa support at mayday@dockwa.com for assistance."
const GENERIC_SERVER_ERROR = {
  type: "server",
  message:
    "Something went wrong settling this payment. Please try again or contact support.",
}

const renderManualPaymentMetadataFields = (paymentType, register) => {
  switch (paymentType) {
    case "check":
      return (
        <>
          <Form.Label htmlFor="check-number">Check number</Form.Label>
          <Form.TextField
            id="check-number"
            {...register("txn.offline_payment_attributes.check_number")}
          />
        </>
      )
    case "manual_ach":
      return (
        <>
          <Form.Label htmlFor="ach-confirmation">ACH confirmation</Form.Label>
          <Form.TextField
            id="ach-confirmation"
            {...register("txn.offline_payment_attributes.ach_confirmation")}
          />
        </>
      )
    case "external_txn":
      return (
        <>
          <Form.Label htmlFor="note">Note</Form.Label>
          <Form.TextField
            id="note"
            {...register("txn.offline_payment_attributes.notes")}
          />
        </>
      )
    default:
      return null
  }
}

const SettlePaymentModal = ({
  payment,
  onPaymentSettle,
  isOpen,
  onClose,
  afterLeave,
  paymentMethods,
}) => {
  const { presentmentCurrency, marinaSlug, currencySymbol } = useMarina()
  const partialSettlementCheckboxRef = useRef()
  const [isPartialSettlement, setIsPartialSettlement] = useState(false)
  const [isCreditCard, setIsCreditCard] = useState(false)
  const [showSurchargeNotice, setShowSurchargeNotice] = useState(false)
  const { contractSurchargeEligible } = useContext(SharedBillingContext) || {}

  const {
    register,
    control,
    watch,
    handleSubmit,
    reset,
    formState: { errors },
    setValue,
    setError,
  } = useForm({
    shouldUnregister: true,
    defaultValues: {
      txn: {
        offline_payment_attributes: {
          payment_type: "cash",
        },
      },
    },
    mode: "all",
  })

  const [paymentType, partialPayment, paymentMethodId] = watch([
    "txn.offline_payment_attributes.payment_type",
    "partialPayment",
    "invoice.payment_method_id",
  ])

  const onResetModal = () => {
    if (afterLeave) {
      afterLeave()
    }
    reset()
    setIsPartialSettlement(false)
  }

  const invoiceId = payment?.id
  const balanceInCents = payment?.balance ?? 0
  const absBalanceInCents = new Decimal(balanceInCents).abs().toNumber()
  const formattedAbsBalance = formatCurrencyFromMinorUnits({
    amount: absBalanceInCents,
    currency: presentmentCurrency,
  })
  const settleType = new Decimal(balanceInCents).lt(0) ? "refund" : "payment"
  const isRefund = settleType === "refund"
  const allowsPartialRefund = Boolean(payment?.allows_partial_refund)
  const billingContainerId = payment?.billing_container_id
  const { cards: paymentCards } = paymentMethods
  const partialPaymentInCents = new Decimal(partialPayment || 0)
    .mul(100)
    .toNumber()
  const existingPaymentMethodId = payment?.payment_method?.id || "manual"
  const existingPaymentMethod = isRefund
    ? String(existingPaymentMethodId)
    : existingPaymentMethodId

  const { mutate, isLoading } = useMutation({
    queryKey: ["invoiceTxn", invoiceId],
    mutationFn: (data) => createInvoiceTxn({ invoiceId, marinaSlug, data }),
    onSuccess: () => {
      onPaymentSettle()
      reset()
    },
    onError: () => {
      setError("root", GENERIC_SERVER_ERROR)
    },
  })

  const {
    isFetching: isLoadingSurchargeCopy,
    isError: isErrorSurchargeCopy,
    data: { surcharge_copy: surchargeCopy },
  } = useQuery(
    ["invoiceSurchargeCopy", payment?.id],
    () => getSurchargeCopy({ invoiceId: payment?.id, marinaSlug }),
    {
      initialData: { surcharge_copy: null },
      enabled: !!(payment?.id && !isRefund),
      refetchOnWindowFocus: false,
      retry: false,
    }
  )

  // Fetching Payment Methods
  const {
    data: paymentMethodResponse,
    isFetching: isLoadingRefundablePaymentMethods,
  } = useGetRefundablePaymentMethods({
    billingContainerId,
    isSettling: true, // include all methods, ignoring scheduled refunds
    options: {
      onSuccess: ({
        refundablePaymentMethods: data,
        isMultiplePaymentRequired,
      }) => {
        if (
          allowsPartialRefund &&
          isMultiplePaymentRequired &&
          !isPartialSettlement
        ) {
          setIsPartialSettlement(true)
        }

        const paymentMethod = data.find(
          (pm) => pm.payment_method_id === String(paymentMethodId)
        )

        // If the current payment method is not refundable, select the first refundable method
        const refundablePaymentMethodId = paymentMethod
          ? paymentMethod.payment_method_id
          : data[0]?.payment_method_id

        if (String(refundablePaymentMethodId) !== String(paymentMethodId)) {
          setValue("invoice.payment_method_id", refundablePaymentMethodId)
        }
      },
      select: (data) => ({
        refundablePaymentMethods: formatPaymentMethods(data),
        isMultiplePaymentRequired: data.every(
          (paymentMethod) => paymentMethod.refundable_amount < absBalanceInCents
        ),
      }),
      onError: (error) => {
        console.log("queryRefundablePaymentMethods error", { error })
      },
      enabled: isRefund,
    },
  })

  const { refundablePaymentMethods, isMultiplePaymentRequired } =
    paymentMethodResponse ?? {
      refundablePaymentMethods: [],
      isMultiplePaymentRequired: null,
    }

  const refundableAmount = useMemo(() => {
    if (!isRefund || refundablePaymentMethods.length === 0) {
      return 0
    }

    const refundablePaymentMethod = refundablePaymentMethods.find(
      (paymentMethod) => paymentMethod.payment_method_id === paymentMethodId
    )

    return refundablePaymentMethod
      ? refundablePaymentMethod.refundable_amount
      : 0
  }, [isRefund, refundablePaymentMethods, paymentMethodId])

  const formattedRefundableAmount = formatCurrencyFromMinorUnits({
    amount: refundableAmount,
    currency: presentmentCurrency,
  })

  useEffect(() => {
    setValue("invoice.payment_method_id", existingPaymentMethod)
  }, [payment, setValue, existingPaymentMethod])

  useEffect(() => {
    const isCreditCard = paymentMethodIsCreditCard(
      "id",
      paymentMethodId,
      paymentCards
    )

    setIsCreditCard(isCreditCard)
  }, [paymentCards, paymentMethodId])

  useEffect(() => {
    setShowSurchargeNotice(surchargeCopy && isCreditCard)
  }, [surchargeCopy, isCreditCard])

  const createInvoiceTransaction = (data, currentPayment) => {
    const requestData = {
      txn: getTxnAttributes({ data, payment: currentPayment }),
    }
    mutate(requestData)
  }

  // invoked for refund creations if the payment method has changed
  // to first update the invoice before creating the transaction
  const { submit: updateInvoice, isLoading: isLoadingInvoice } =
    useUpdateInvoice({
      paymentId: payment?.id,
      onSuccess: (payment) =>
        handleSubmit((data) => createInvoiceTransaction(data, payment))(),
      onError: () => {
        setError("root", GENERIC_SERVER_ERROR)
      },
    })

  const onSubmit = (data) => {
    if (String(existingPaymentMethod) !== String(paymentMethodId)) {
      if (isRefund) {
        updateInvoice(data)
      } else {
        createInvoiceTransaction(data, {
          ...payment,
          payment_method: {
            ...(payment?.payment_method ? payment?.payment_method : {}),
            id: getRequestPaymentMethodId(paymentMethodId),
          },
        })
      }
    } else {
      createInvoiceTransaction(data, payment)
    }
  }

  const renderSurchargeNotice = () => {
    if (showSurchargeNotice) {
      return (
        <div className="mb-5">
          <AlertBanner type="info">{surchargeCopy}</AlertBanner>
        </div>
      )
    }
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      maxSize="medium"
      afterLeave={onResetModal}
    >
      <Modal.Header title="Settle now" />
      <Modal.Body>
        <ReloadableWidget isLoading={isLoadingSurchargeCopy} isError={false}>
          {isRefund && allowsPartialRefund && isMultiplePaymentRequired && (
            <div className="mb-5">
              <AlertBanner type="info">
                There is a total of
                <strong>{` ${formattedAbsBalance} `}</strong>
                owed to the customer. You can refund up to
                <strong>{` ${formattedRefundableAmount} `}</strong>
                on this payment method. Please settle the partial amount and
                refund the rest separately
              </AlertBanner>
            </div>
          )}
          <div className="mb-5 w-3/4">
            {isRefund ? (
              <RefundPaymentMethodDropdown
                id="refund-payment-method"
                name="invoice.payment_method_id"
                control={control}
                isLoading={isLoadingRefundablePaymentMethods}
                paymentMethods={refundablePaymentMethods}
                registerOptions={{
                  validate: (val) =>
                    val ? null : "Refund method is required.",
                }}
                errors={errors?.invoice?.payment_method_id}
              />
            ) : (
              <PaymentMethodDropdown
                name="invoice.payment_method_id"
                onlineMethods={paymentCards}
                control={control}
                includeExpirationDates
                disabled={isRefund}
                existingPaymentMethod={payment?.payment_method}
              />
            )}
          </div>
          {renderSurchargeNotice()}
          {isErrorSurchargeCopy && contractSurchargeEligible && isCreditCard ? (
            <div className="my-5">
              <Form.Error>{SURCHARGE_ELIGIBILITY_CHECK_ERROR}</Form.Error>
            </div>
          ) : null}
          {paymentMethodId === "manual" && (
            <div className="mb-5 flex w-full flex-row gap-4">
              <div className="flex w-1/2 flex-col">
                <Form.Label htmlFor="manual-payment-type">
                  Manual payment type
                </Form.Label>
                <Form.Select
                  id="manual-payment-type"
                  {...register("txn.offline_payment_attributes.payment_type")}
                >
                  {Object.entries(OFFLINE_PAYMENT_METHODS).map(
                    ([value, displayName]) => (
                      <option key={value} value={value}>
                        {displayName}
                      </option>
                    )
                  )}
                </Form.Select>
              </div>
              <div className="flex w-1/2 flex-col">
                {renderManualPaymentMetadataFields(paymentType, register)}
              </div>
            </div>
          )}
          {!isPartialSettlement ? (
            <div className="flex flex-row gap-4">
              <div className="mb-5 flex w-1/2 flex-col">
                <Form.Label htmlFor="full-payment-amount">
                  {capitalize(settleType)} amount
                </Form.Label>
                <Form.IconTextField
                  id="full-payment-amount"
                  position="left"
                  icon={currencySymbol}
                  type="number"
                  disabled
                  value={new Decimal(
                    isRefund ? balanceInCents : absBalanceInCents
                  )
                    .div(100)
                    .toFixed(2)}
                />
              </div>
              <div className="mb-5 w-1/2"></div>
            </div>
          ) : (
            <div className="mb-5 flex flex-row gap-4">
              <div className="flex w-full flex-col">
                <Form.Label htmlFor="partial-payment-amount">
                  {isRefund
                    ? "Partial refund amount"
                    : "Partial payment amount"}
                </Form.Label>
                <div className="-mt-2 mb-2">
                  <MaxPaymentAmountDisplay
                    amount={isRefund ? refundableAmount : absBalanceInCents}
                    displayZero
                  />
                </div>
                <Form.IconTextField
                  id="partial-payment-amount"
                  position="left"
                  icon={currencySymbol}
                  type="number"
                  {...register("partialPayment", {
                    validate: (val) => {
                      const amount = new Decimal(val || 0).mul(100).toNumber()
                      if (!amount || amount < 0) {
                        return "Partial payment amount must be greater than 0."
                      }
                      if (isRefund && amount > refundableAmount) {
                        return `Partial ${settleType} cannot exceed ${settleType} amount of ${formattedRefundableAmount}.`
                      }
                      if (amount > absBalanceInCents) {
                        return `Partial payment cannot exceed ${settleType} amount of ${formattedAbsBalance}.`
                      }
                    },
                  })}
                  hasErrors={!!errors.partialPayment}
                />
                {errors.partialPayment && (
                  <Form.Error>{errors.partialPayment.message}</Form.Error>
                )}
              </div>
              <div className="flex w-full flex-col">
                <Form.Label htmlFor="remainder-payment-amount">
                  Remaining amount
                </Form.Label>
                <div className="text-muted -mt-2 mb-2">
                  (scheduled separately)
                </div>
                <Form.IconTextField
                  id="remainder-payment-amount"
                  position="left"
                  icon={currencySymbol}
                  type="number"
                  disabled
                  value={
                    errors.partialPayment
                      ? ""
                      : new Decimal(absBalanceInCents)
                          .minus(partialPaymentInCents)
                          .div(100)
                          .toFixed(2)
                  }
                />
              </div>
            </div>
          )}
          {(allowsPartialRefund || !isRefund) && (
            <div className="mb-5">
              <Form.Checkbox
                name="partial-settlement"
                ref={partialSettlementCheckboxRef}
                onChange={() => {
                  setIsPartialSettlement(!isPartialSettlement)
                }}
                checked={isPartialSettlement}
                disabled={isMultiplePaymentRequired}
                compact
                label="Settle partial amount"
              />
            </div>
          )}
        </ReloadableWidget>
      </Modal.Body>
      <Modal.Footer>
        <div className="flex flex-col justify-end gap-4">
          <div className="flex justify-end">
            <div className="mr-5">
              <Button variant="tertiary" onClick={onClose}>
                Cancel
              </Button>
            </div>
            <Button
              type="submit"
              variant="primary"
              onClick={handleSubmit(onSubmit)}
              isLoading={
                isLoading ||
                isLoadingInvoice ||
                isLoadingRefundablePaymentMethods ||
                isLoadingSurchargeCopy
              }
            >
              {isPartialSettlement ? "Settle partial amount" : "Settle now"}
            </Button>
          </div>
          {errors.root ? <Form.Error>{errors.root.message}</Form.Error> : null}
        </div>
      </Modal.Footer>
    </Modal>
  )
}

SettlePaymentModal.propTypes = {
  payment: PropTypes.shape({
    id: PropTypes.string.isRequired,
    billing_container_id: PropTypes.oneOfType([
      PropTypes.number,
      PropTypes.string,
    ]),
    allows_partial_refund: PropTypes.bool,
    balance: PropTypes.number.isRequired,
    payment_method: PropTypes.object,
  }),
  onPaymentSettle: PropTypes.func.isRequired,
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  afterLeave: PropTypes.func,
  paymentMethods: PropTypes.shape({
    cards: PropTypes.array.isRequired,
  }),
}

export default SettlePaymentModal
